<template>
  <div class="inputDropdown dropdown" v-show="showComponent">
    <button
      class="dropdown-button"
      @click="active = !active"
      :class="{ 'transparent-button': transparent }"
      :style="`background-color:${backgroundColor}`"
    >
      <awesomeIcon icon="sort" /> {{ display }}
    </button>
    <transition name="fade">
      <div class="dropdown-bg" v-if="active" @click="active = false"></div>
    </transition>
    <transition name="fade">
      <div class="dropdown-body" :class="{
          'scrollable': scroll,
          'align-right': align === 'right',
        }">
        <ul class="dropdown-list" v-if="active">
          <li
            v-if="clearable"
            @click="select(null)"
            title="Selectie wissen"
          >Wissen</li>
          <li
            v-for="(item, index) in array"
            :key="index"
            @click="select(item)"
            :class="{ 'disabled': !item.label && !item.value }"
          >
            <span v-if="item.label || item.value">{{ item.label ? item.label : item.value }}</span>
            <span v-else/>
            <span v-if="item.rightLabel" class="right-label">{{ item.rightLabel }}</span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
<script>
import { find } from 'lodash-es';

export default {
  name: 'InputDropdown',
  emits: ['update:model-value'],
  props: {
    array: {
      type: Array,
      default: () => [],
    },
    cb: {
      type: Function,
      default: () => {},
    },
    modelValue: {
      type: null,
    },
    defaultValue: {
      type: String,
      default: '',
    },
    defaultIndex: {
      type: Number,
      default: 0,
    },
    displayPrefix: {
      type: String,
      default: '',
    },
    displayPostfix: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    scroll: {
      type: Boolean,
      default: false,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    requirements: {
      type: Function,
      default: () => true,
    },
  },
  data() {
    return {
      active: false,
      display: null,
      lastSelected: null,
    };
  },
  computed: {
    showComponent() {
      if (this.requirements === null) {
        return true;
      }

      return this.requirements();
    },
  },
  watch: {
    modelValue() {
      if (this.modelValue === this.lastSelected) {
        return;
      }

      for (let i = 0; i < this.array.length; i += 1) {
        if (this.array[i].value === this.modelValue) {
          this.select(this.array[i]);
          return;
        }
      }

      this.select(null);
    },
    array: {
      handler() {
        this.setDisplay('update');
      },
      deep: true,
    },
  },
  created() {
    if (![this.modelValue, this.array].includes(undefined) && this.modelValue !== null) {
      const item = find(this.array, { value: this.modelValue });
      this.select(item);
    } else if (this.modelValue === null && find(this.array, { value: this.modelValue })) {
      const itemNull = find(this.array, { value: this.modelValue });
      this.select(itemNull);
    } else {
      this.setDisplay(null);
    }

    if (this.defaultValue !== '') {
      this.display = this.defaultValue;
    }

    if (this.defaultValue) {
      for (let i = 0; i < this.array.length; i += 1) {
        if (this.array[i].value === this.defaultValue) {
          this.select(this.array[i]);
          return;
        }
      }
    }
  },
  methods: {
    setDisplay(item) {
      if (item === null) {
        if (this.defaultValue) {
          this.select(find(this.array, { value: this.defaultValue }));
        }
        return;
      }

      if (
        typeof item !== 'object'
        && ![this.modelValue, this.array].includes(undefined)
        && this.modelValue !== null
      ) {
        item = find(this.array, { value: this.modelValue });
      } else if ([null, 'update'].includes(item) && this.lastSelected === null) {
        item = this.array[this.defaultIndex];
      } else if (item === 'update') {
        item = find(this.array, { value: this.lastSelected });
      }
      if (typeof item === 'object') {
        const postfix = this.displayPostfix === null ? '' : this.displayPostfix;
        let prefix = this.displayPrefix;
        if (this.displayPrefix === null) {
          prefix = '';
        }
        this.display = `${prefix}${item.label ? item.label : item.value}${postfix}`;
      }
    },
    select(item) {
      if (item === undefined || (item !== null && !('label' in item) && !('value' in item) && item.rightLabel)) {
        return;
      }

      this.active = false;
      this.lastSelected = item === null ? null : item.value;
      this.setDisplay(item);

      if (item === null) {
        this.$emit('update:model-value', null);
      } else {
        this.$emit('update:model-value', item.value);
      }

      if (this.cb) {
        this.$nextTick(() => {
          this.cb(item);
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.inputDropdown {
  padding: 0;

  button {
    border-radius: 0;
    white-space: nowrap;
    padding: 10px;
    overflow: hidden;

    &.transparent-button {
      background-color: transparent;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }

  .dropdown-body {
    width: auto;
    height: auto;
    position: absolute;
    z-index: 99;
    border-radius: 6px;
    overflow: hidden;

    &.align-right {
      right: 0px;
    }

    &.scrollable {
      max-height: 300px;
      overflow-y: scroll;
    }

    ul {
      display: table;
      min-width: 80px;
      position: relative;
      border-radius: 0;

      li {
        white-space: nowrap;
        display: flex;
        justify-content: space-between;

        &.disabled {
          cursor: default;
          background-color: transparent;
        }

        &:last-child span.right-label {
          cursor: text;
        }

        span.right-label {
          color: rgba(#000, 0.5);
          margin-left: 15px;
        }
      }
    }
  }
}
</style>
